import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from '../../../components/bootstrap/Button';
import Spinner from '../../../components/bootstrap/Spinner';
import { toast } from 'react-toastify';
import useDarkMode from '../../../hooks/useDarkMode';
import CustomTextInput from '../CustomTextInput';
import authAPI from '../../API/authAPI';
import Icon from '../../../components/icon/Icon';

interface ResetPasswordComponentProps {
	email: string;
	onBack: () => void;
	onSuccess: () => void;
}

const ResetPasswordComponent = ({ email, onBack, onSuccess }: ResetPasswordComponentProps) => {
	const { darkModeStatus } = useDarkMode();
	const [isLoading, setIsLoading] = useState(false);

	const getPasswordStrength = (password: string) => {
		let score = 0;
		if (password.length >= 6) score += 1;
		if (password.length >= 8) score += 1;
		if (/[A-Z]/.test(password)) score += 1;
		if (/[a-z]/.test(password)) score += 1;
		if (/\d/.test(password)) score += 1;
		if (/[^A-Za-z0-9]/.test(password)) score += 1;
		
		if (score < 2) return { strength: 'Weak', color: 'danger' };
		if (score < 4) return { strength: 'Fair', color: 'warning' };
		if (score < 5) return { strength: 'Good', color: 'info' };
		return { strength: 'Strong', color: 'success' };
	};

	const formik = useFormik({
		initialValues: {
			newPassword: '',
			confirmPassword: '',
		},
		validationSchema: Yup.object().shape({
			newPassword: Yup.string()
				.required('New password is required')
				.min(6, 'Password must be at least 6 characters'),
			confirmPassword: Yup.string()
				.required('Please confirm your password')
				.oneOf([Yup.ref('newPassword')], 'Passwords must match'),
		}),
		onSubmit: async (values) => {
			setIsLoading(true);
			try {
				const data = await authAPI.resetPassword({
					email,
					newPassword: values.newPassword,
					confirmPassword: values.confirmPassword,
				});

				if (data.success) {
					toast.success(data.message || 'Password reset successfully', {
						position: 'top-right',
						autoClose: 5000,
						theme: darkModeStatus ? 'dark' : 'light',
					});
					onSuccess();
				} else {
					toast.error(data.message || 'Failed to reset password', {
						position: 'top-right',
						autoClose: 5000,
						theme: darkModeStatus ? 'dark' : 'light',
					});
				}
			} catch (error: any) {
				const errorMessage = error?.response?.data?.message || error?.message || 'Failed to reset password. Please try again.';
				toast.error(errorMessage, {
					position: 'top-right',
					autoClose: 5000,
					theme: darkModeStatus ? 'dark' : 'light',
				});
			} finally {
				setIsLoading(false);
			}
		},
	});

	return (
		<div className="reset-password-form">
			<div className="text-center mb-4">
				<Icon icon="VpnKey" size="3x" color="success" className="mb-3" />
				<h4 className="mb-2">Create New Password</h4>
				<p className="text-muted">
					Please enter a strong new password for <br />
					<strong>{email}</strong>
				</p>
			</div>

			<form onSubmit={formik.handleSubmit}>
				<div className="mb-3">
					<CustomTextInput
						id="newPassword"
						type="password"
						placeholder="Enter new password"
						autoComplete="new-password"
						value={formik.values.newPassword}
						isTouched={formik.touched.newPassword}
						invalidFeedback={formik.errors.newPassword}
						isValid={formik.isValid}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
					/>
					{formik.values.newPassword && (
						<div className="mt-2">
							<small className={`text-${getPasswordStrength(formik.values.newPassword).color}`}>
								Password strength: {getPasswordStrength(formik.values.newPassword).strength}
							</small>
						</div>
					)}
				</div>

				<div className="mb-4">
					<CustomTextInput
						id="confirmPassword"
						type="password"
						placeholder="Confirm new password"
						autoComplete="new-password"
						value={formik.values.confirmPassword}
						isTouched={formik.touched.confirmPassword}
						invalidFeedback={formik.errors.confirmPassword}
						isValid={formik.isValid}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
					/>
				</div>

				<div className="d-grid gap-2">
					<Button
						type="submit"
						color="success"
						className="py-3"
						isDisable={isLoading || !formik.isValid}
					>
						{isLoading && <Spinner className="me-2" isSmall inButton isGrow />}
						Update Password
					</Button>
					
					<Button
						type="button"
						color="light"
						className="py-3"
						onClick={onBack}
						isDisable={isLoading}
					>
						<Icon icon="ArrowBack" className="me-2" />
						Back
					</Button>
				</div>
			</form>
		</div>
	);
};

export default ResetPasswordComponent;